import { TIE_STEP, defaultPlayersAbove, frameId, pictureLayerOrder, propDrawRow, type AssetManifest, type ChunkProp, type CompiledChunk, type ManifestObjectAppearance } from "daedalus-core";
import type { PictureLayer } from "daedalus-renderer-phaser";
import { ShrineArt, landmarkProps, propReaches, type KeyedProp, type LandmarkTally } from "../map-picture/landmarks.ts";

/**
 * The game's side of the map picture (ADR-100 decision 3): turns the client chunks the page already holds into the
 * layers the shared picture painter draws, in drawn order. A tile layer is its cells. A prop layer is the props of the
 * chunk and of the eight beside it that reach into the chunk, since a compiled chunk keeps a prop only in the chunk of
 * its anchor and the painter clips a chunk to its own square; each in the order the page's props sort in. A roof's
 * prop is drawn like any other, with its own closed frame, whole whoever stands under it, since the picture is never
 * handed the presence playback (decision 55). The things come from the chunks' landmark lists and the shrine art the
 * character has discovered, through the one function the minimap's feed uses (`landmarkProps`), and join the prop layer
 * of their own place by the key props sort by (decision 59). The layers come in the order the world draws them (`pictureLayerOrder`): a terrain layer is flat art under every prop, so it is laid before the
 * props even where the chunk lists it after them. The band is the chunk's own (`defaultPlayersAbove`), not the map's
 * pinned one, which counts the document's layers and not the chunk's. This is the one place the picture meets the
 * game, so the painter, the fade and the cache can move to a shared package as they are.
 */

/**
 * What the feed reads: the client chunks by `cx,cy`, the asset manifest, the world version's appearance table, and the
 * map's chunk and tile sizes.
 */
export interface ChunkFeedSource {
  readonly chunks: ReadonlyMap<string, CompiledChunk>;
  readonly manifest: AssetManifest;
  readonly appearances: readonly ManifestObjectAppearance[];
  readonly chunkSize: number;
  readonly tileSize: number;
  readonly art?: ShrineArt | null;
  readonly tally?: LandmarkTally;
}

/**
 * Makes the feed of a map's chunks.
 *
 * @param source The chunks and what they are drawn against.
 * @returns A function giving the layers of a chunk in drawn order, none for a chunk the map lacks. It throws nothing
 *   for a prop or cell whose frame the manifest lacks: the painter counts those.
 */
export function chunkFeed(source: ChunkFeedSource): (cx: number, cy: number) => readonly PictureLayer[] {
  const { chunks, manifest, appearances, chunkSize, tileSize } = source;
  const art = source.art ?? null;

  /**
   * The props of one layer that reach into a chunk, from the chunk and its eight neighbours, with the landmarks of that
   * layer, sorted to draw.
   *
   * @param layerId The layer's id.
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @param extra The landmarks that draw at this layer.
   * @returns The props in drawn order.
   */
  const propsReaching = (layerId: string, cx: number, cy: number, extra: readonly KeyedProp[]): readonly KeyedProp["prop"][] => {
    const left = cx * chunkSize;
    const top = cy * chunkSize;
    const found: KeyedProp[] = [...extra];
    for (let ny = cy - 1; ny <= cy + 1; ny += 1) {
      for (let nx = cx - 1; nx <= cx + 1; nx += 1) {
        const layer = chunks.get(`${nx},${ny}`)?.layers.find((candidate) => candidate.kind === "prop" && candidate.id === layerId);
        if (layer === undefined || layer.kind !== "prop") {
          continue;
        }
        for (const prop of layer.props as readonly ChunkProp[]) {
          const id = frameId(prop.frame.tileset, null, prop.frame.tile);
          const rect = manifest.frames[id];
          if (rect === undefined) {
            continue;
          }
          if (!propReaches(prop.x, prop.y, rect.w, rect.h, tileSize, left, top, chunkSize)) {
            continue;
          }
          found.push({ prop: { frame: id, x: prop.x, y: prop.y, flipX: prop.flipX, flipY: prop.flipY }, key: propDrawRow(prop, rect, tileSize) + (prop.order ?? 0) * TIE_STEP });
        }
      }
    }
    return found.sort((a, b) => a.key - b.key).map((entry) => entry.prop);
  };

  return (cx, cy) => {
    const chunk = chunks.get(`${cx},${cy}`);
    if (chunk === undefined) {
      return [];
    }
    const layers: PictureLayer[] = [];
    const band = defaultPlayersAbove(chunk.layers);
    const things = landmarkProps({ manifest, appearances, tileSize, chunkSize }, chunks, art, band, cx, cy, source.tally);
    for (const index of pictureLayerOrder(chunk.layers.map((layer) => layer.kind === "tile" && layer.variant !== null), band)) {
      const layer = chunk.layers[index] as CompiledChunk["layers"][number];
      if (layer.kind === "tile") {
        layers.push({ kind: "cells", cells: { tiles: layer.tiles, chunkSize, tileSize, tilesets: chunk.tilesets, variant: layer.variant, manifest } });
      }
      if (layer.kind === "prop") {
        layers.push({ kind: "props", props: propsReaching(layer.id, cx, cy, things.get(index) ?? []) });
      } else if (things.has(index)) {
        layers.push({ kind: "props", props: (things.get(index) ?? []).map((entry) => entry.prop) });
      }
    }
    return layers;
  };
}
